


































































































import { Component, Vue } from 'vue-property-decorator'
import { Pricing } from '@/models'
import { confirmPricingOverlaps } from '@/utils/pricing-overlap-confirm'

const LABELS: Record<string, string> = {
  card_issuance: 'Émission',
  card_loading: 'Chargement'
}

interface PricingPeriodRow {
  key: string
  caretakerId: string | null
  caretaker?: Pricing['caretaker']
  validFrom: string
  issuance?: Pricing
  loading?: Pricing
}

@Component({ name: 'CardPricing' })
export default class extends Vue {
  pricings: Pricing[] = []
  loading = false
  searchQuery = ''

  dialogVisible = false
  dialogPricingItem = ''
  dialogValue = ''
  dialogValidFrom: Date | null = null
  dialogValidTo: Date | null = null
  dialogLoading = false

  get items() {
    return Object.keys(LABELS).map(pricingItem => ({
      pricingItem,
      label: LABELS[pricingItem],
      current: this.pricings.find(
        p => p.pricingItem === pricingItem && !p.caretakerId && p.status === 'active' && this.isCurrent(p)
      )
    }))
  }

  get history(): PricingPeriodRow[] {
    const rows = new Map<string, PricingPeriodRow>()

    this.pricings.forEach(pricing => {
      const key = `${pricing.caretakerId || 'default'}__${pricing.validFrom}`
      const row = rows.get(key) || {
        key,
        caretakerId: pricing.caretakerId,
        caretaker: pricing.caretaker,
        validFrom: pricing.validFrom
      }

      if (pricing.pricingItem === 'card_issuance') row.issuance = pricing
      else row.loading = pricing

      rows.set(key, row)
    })

    const query = this.searchQuery.trim().toLowerCase()
    const filtered = query
      ? [...rows.values()].filter(row => this.caretakerName(row).toLowerCase().includes(query))
      : [...rows.values()]

    return filtered.sort((a, b) => {
      const byCaretaker = this.caretakerSortKey(a).localeCompare(this.caretakerSortKey(b))
      return byCaretaker !== 0 ? byCaretaker : b.validFrom.localeCompare(a.validFrom)
    })
  }

  caretakerName(row: PricingPeriodRow) {
    return row.caretakerId ? row.caretaker?.name || row.caretakerId : 'Tarif par défaut'
  }

  caretakerSortKey(row: PricingPeriodRow) {
    return row.caretakerId ? this.caretakerName(row) : ''
  }

  rowValidTo(row: PricingPeriodRow) {
    return row.issuance?.validTo ?? row.loading?.validTo ?? null
  }

  rowIsCurrent(row: PricingPeriodRow) {
    return [row.issuance, row.loading].every(pricing => !pricing || this.isCurrent(pricing))
  }

  created() {
    this.load()
  }

  isCurrent(pricing: Pricing) {
    const now = new Date()
    const from = new Date(pricing.validFrom)
    const to = pricing.validTo ? new Date(pricing.validTo) : null

    return from <= now && (!to || to > now)
  }

  labelFor(pricingItem: string) {
    return LABELS[pricingItem] || pricingItem
  }

  formatValue(pricing: Pricing) {
    return pricing.unit === 'percentage'
      ? this.$n(pricing.value, { style: 'percent', minimumFractionDigits: 0, maximumFractionDigits: 2 })
      : this.$n(pricing.value, 'currency')
  }

  async load() {
    this.loading = true
    const { data } = await Pricing.includes('caretaker').per(200).all()
    this.pricings = data
    this.loading = false
  }

  openDialog(pricingItem: string) {
    this.dialogPricingItem = pricingItem
    this.dialogValue = ''
    this.dialogValidFrom = null
    this.dialogValidTo = null
    this.dialogVisible = true
  }

  async submit() {
    const value = this.dialogPricingItem === 'card_loading' ? Number(this.dialogValue) / 100 : Number(this.dialogValue)
    const validFrom = this.dialogValidFrom ? this.dialogValidFrom.toISOString() : undefined
    const validTo = this.dialogValidTo ? this.dialogValidTo.toISOString() : undefined

    this.dialogLoading = true
    try {
      const confirmed = await confirmPricingOverlaps({ pricingItem: this.dialogPricingItem, value, validFrom, validTo })
      if (!confirmed) return

      const pricing = new Pricing({ pricingItem: this.dialogPricingItem, value, validFrom, validTo })

      await pricing.save()

      this.$notify({ title: 'Succès', message: 'Tarif mis à jour', type: 'success', duration: 2000 })
      this.dialogVisible = false
      this.load()
    } catch (err: any) {
      const errors = err?.response?.data?.errors
      this.$notify({
        title: 'Erreur',
        message: errors?.length ? errors[0].detail : 'Mise à jour impossible',
        type: 'error',
        duration: 3000
      })
    } finally {
      this.dialogLoading = false
    }
  }
}
